import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getLocalizedPath } from "../utils/urlLocalization";

interface CookieConsent {
  ad: boolean;
  analytics: boolean;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Add 3-second delay before showing the banner
    const timer = setTimeout(() => {
      const consent = localStorage.getItem("cookieConsent");
      if (!consent) {
        setVisible(true);
      } else {
        const parsed = JSON.parse(consent) as CookieConsent;
        applyConsent(parsed);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const applyConsent = (consent: CookieConsent) => {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", {
        ad_storage: consent.ad ? "granted" : "denied",
        analytics_storage: consent.analytics ? "granted" : "denied",
      });
    }
  };

  const handleConsent = (type: "accept" | "reject") => {
    const consent: CookieConsent =
      type === "accept"
        ? { ad: true, analytics: true }
        : { ad: false, analytics: false };

    localStorage.setItem("cookieConsent", JSON.stringify(consent));
    applyConsent(consent);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 w-full bg-white shadow-lg border-t border-gray-200 p-6 z-50">
      <div className="container mx-auto max-w-4xl">
        <div className="space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg">{t('cookies.banner.title')}</h3>
            <p className="text-gray-600">
              {t('cookies.banner.description')}{" "}
              <Link
                to={getLocalizedPath('cookies', i18n.language)}
                className="text-blue-600 hover:text-blue-800"
              >
                {t('cookies.banner.policyLink')}
              </Link>
            </p>
            <p className="text-gray-600">
              {t('cookies.banner.choice')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={() => handleConsent("reject")}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors text-center"
            >
              {t('cookies.banner.acceptEssential')}
            </button>
            <button
              onClick={() => handleConsent("accept")}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              {t('cookies.banner.acceptAll')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner; 